import { useCallback, useMemo } from 'react';

import { Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { Column } from 'react-table';
import { useQueryParams } from 'use-query-params';

import { fetchOrganizations } from 'api';
import { LinkButton, Link } from 'components';
import { Table } from 'components/Table';
import { getPageCount } from 'helpers';
import { createEnumParam, StringParam } from 'helpers/useQueryParams';
import { useControlledPagination, useUrlPagination } from 'hooks';
import { CasesValidatedCell } from 'pages/organization_edit';
import {
  AssignedOption,
  OrganizationFullProps,
  OrganizationRelationshipOption,
  SortOptions,
  Employer,
} from 'types';

import { CellWrapper, DashboardWrapper } from '../styledComponents';
import { OrganizationTableSidebar } from './OrganizationTableSidebar';

const filterConfig = {
  name: StringParam,
  type: createEnumParam<OrganizationRelationshipOption>(['top', 'associated']),
  assigned: createEnumParam<AssignedOption>(['assigned', 'unassigned']),
  sort_order: createEnumParam<SortOptions>(['ascending', 'descending']),
  is_employer: createEnumParam<Employer>(['true', 'false', '']),
  country: StringParam,
};
export const AdminOrganizationTable = () => {
  const { controlledRows, controlledPageIndex } = useUrlPagination();
  const location = useLocation();
  const urlFrom = useMemo(
    () => location.pathname + location.search,
    [location.pathname, location.search],
  );
  const [filters, setFilters] = useQueryParams(filterConfig, {
    updateType: 'replaceIn',
  });

  const config = useMemo(() => {
    return {
      params: {
        page_number: controlledPageIndex,
        limit: controlledRows,
        fuzzy: true,
        ...filters,
      },
    };
  }, [filters, controlledPageIndex, controlledRows]);
  const { dataArray, dataLoaded, count } = useControlledPagination(
    fetchOrganizations,
    config,
  );
  const globalFiltersApplied = useMemo(
    () => Object.entries(filters).some(([, value]) => value !== undefined),
    [filters],
  );
  const { updateControlledPageIndex } = useUrlPagination();

  // update and return to first page when filter is applied
  const wrapSetOrganizationFilter = useCallback(
    (arg: Parameters<typeof setFilters>[0]) => {
      setFilters(arg);
      updateControlledPageIndex(1);
    },
    [setFilters, updateControlledPageIndex],
  );

  const columns = useMemo(
    (): Array<Column<OrganizationFullProps>> => [
      {
        id: 'edit',
        Header: () => {
          return (
            <CellWrapper aria-label='Edit Organization'>
              <p />
            </CellWrapper>
          );
        },
        width: 60,
        Cell: (row: any) => {
          return (
            <CellWrapper>
              <LinkButton
                variant='text'
                to={{
                  pathname: `organizations/${row.row.original.id}`,
                  state: { from: urlFrom },
                }}
                aria-label={'Edit organization ' + row.row.original.name}
              >
                EDIT
              </LinkButton>
            </CellWrapper>
          );
        },
      },
      {
        id: 'employers',
        Header: 'Employer',
        width: 300,
        accessor: 'name',
        Cell: (row: any) => {
          const { name, parent_id } = row.row.original;
          return (
            <>
              <Typography variant='body1'>{name}</Typography>
              {parent_id ? (
                <Typography color='green' variant='subtitle1'>
                  ASSOCIATED ENTITY
                </Typography>
              ) : (
                <Typography color='blue' variant='subtitle1'>
                  ORGANIZATION
                </Typography>
              )}
            </>
          );
        },
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'associated_entities',
        Header: 'Associated Entities',
        Cell: (row: any) => {
          const { children } = row.row.original;
          return (
            <CellWrapper>
              <Typography variant='body1'>{children.length}</Typography>
            </CellWrapper>
          );
        },
        width: 100,
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'users',
        Header: 'Users',
        Cell: (row: any) => {
          const { parent_id, users } = row.row.original;
          return (
            <CellWrapper>
              {!parent_id && !users.length ? (
                <Typography color='error' variant='body1'>
                  {users.length}
                </Typography>
              ) : (
                <Typography variant='body1'>{users.length}</Typography>
              )}
            </CellWrapper>
          );
        },
        width: 100,
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'reviewable_candidates',
        Header: 'Candidates To Review',
        Cell: (row: any) => {
          const organization = row.row.original as OrganizationFullProps;
          const { id, reviewable_candidate_count } = organization;
          return (
            <CellWrapper>
              <Link
                variant='body1'
                sx={{
                  cursor: 'pointer',
                }}
                to={`reviewed_candidates?organization_id=${id}`}
              >
                {reviewable_candidate_count}
              </Link>
            </CellWrapper>
          );
        },
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'cases_validated',
        Header: 'Cases Validated',
        width: 100,
        Cell: (row: any) => {
          const organization = row.row.original as OrganizationFullProps;

          return (
            <CellWrapper>
              <CasesValidatedCell {...organization} />
            </CellWrapper>
          );
        },
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'associated_candidates',
        Header: 'Associated Candidates',
        Cell: (row: any) => {
          const organization = row.row.original as OrganizationFullProps;
          const { candidate_count } = organization;
          return (
            <CellWrapper>
              <Typography variant='body1'>{candidate_count}</Typography>
            </CellWrapper>
          );
        },
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'country',
        Header: 'Location',
        Cell: (row: any) => {
          const { country } = row.row.original;
          return (
            <CellWrapper>
              <Typography variant='body1'>{country || 'Unknown'}</Typography>
            </CellWrapper>
          );
        },
        width: 100,
        disableFilters: true,
        disableSortBy: false,
      },
    ],
    [urlFrom],
  );

  return (
    <DashboardWrapper>
      <OrganizationTableSidebar
        setFilters={wrapSetOrganizationFilter}
        filters={filters}
      />

      <Table<OrganizationFullProps>
        globalFiltersApplied={globalFiltersApplied}
        handleGlobalFilterClear={() => {
          wrapSetOrganizationFilter({
            name: undefined,
            sort_order: undefined,
            assigned: undefined,
            type: undefined,
            country: undefined,
            is_employer: undefined,
          });
        }}
        loading={!dataLoaded}
        count={count}
        useTableOptions={{
          data: dataArray,
          columns: columns,
          manualPagination: true,
          pageCount: getPageCount(count, controlledRows),
          autoResetPage: false,
        }}
      />
    </DashboardWrapper>
  );
};
